import { useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import Cookies from 'js-cookie';
import { loginRequest } from './msalConfig';

/**
 * Drop-in replacement for useAuth0() from @auth0/auth0-react.
 * Exposes the same interface so components don't need structural changes.
 */
export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] || null;

  const isLoading = inProgress !== InteractionStatus.None;

  const user = account
    ? {
        name: account.name || account.idTokenClaims?.name || account.username,
        email:
          account.idTokenClaims?.email ||
          account.idTokenClaims?.preferred_username ||
          account.username,
        email_verified: true,
      }
    : null;

  // Compatible with Auth0's logout({ logoutParams: { returnTo: '...' } })
  const logout = useCallback(
    (options) => {
      localStorage.removeItem('access_token');
      Cookies.remove('access_token');
      const returnTo =
        options?.logoutParams?.returnTo ||
        options?.postLogoutRedirectUri ||
        window.location.origin;
      instance.logoutRedirect({ postLogoutRedirectUri: returnTo });
    },
    [instance],
  );

  // Compatible with Auth0's loginWithRedirect({ appState: { returnTo } })
  const loginWithRedirect = useCallback(
    (options) => {
      const returnTo = options?.appState?.returnTo;
      if (returnTo) sessionStorage.setItem('msal_returnTo', returnTo);
      instance.loginRedirect({ ...loginRequest });
    },
    [instance],
  );

  // Compatible with Auth0's getAccessTokenSilently()
  const getAccessTokenSilently = useCallback(
    async (_options) => {
      if (!account) throw new Error('No authenticated account found');
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.accessToken;
    },
    [instance, account],
  );

  return {
    isAuthenticated,
    isLoading,
    user,
    logout,
    loginWithRedirect,
    getAccessTokenSilently,
  };
}
